import { useState, useMemo, useEffect, useCallback } from 'react';
import { Megaphone, ChevronDown, ChevronRight, Check } from 'lucide-react';

import { calculateSegmentPackageDemand, calculatePnL } from '../../lib/calculations';
import { computeBuyerProxy } from '../../lib/buyerProxy';
import { VERBATIM_DATA } from '../../data/verbatimData';
import { useSavedPackages } from '../../hooks/useSavedPackages';
import { useMarketingPersistence } from '../../hooks/useMarketingPersistence';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { InfoButton } from '../common/Tooltip';
import { SavedPackageSelector } from './SavedPackageSelector';
import { MarketSizingPanel } from './MarketSizingPanel';
import { AudiencePersonaCard } from './AudiencePersonaCard';
import { StrategicDirections } from './StrategicDirections';
import { MarketingWorkspace } from './MarketingWorkspace';
import type { SavedPackage } from '../../hooks/useSavedPackages';
import type { StrategicDirection } from '../../lib/strategicDirections';
import type { PackageConfig } from '../../types';

function StepHeader({ step, title, done, open, onToggle }: { step: number; title: string; done: boolean; open: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} className="w-full flex items-center gap-3 py-3 text-left group">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
        done ? 'bg-chart-teal text-white' : 'bg-surface border-2 border-border text-muted group-hover:border-chart-teal/50'
      }`}>
        {done ? <Check size={14} /> : step}
      </div>
      <span className={`text-sm font-semibold flex-1 ${done ? 'text-primary' : 'text-secondary'}`}>{title}</span>
      {open ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
    </button>
  );
}

type AdResult = { image: string; mimeType: string; format: string };

export function MarketingStrategyTab() {
  const { packages: savedPkgs, loading: pkgsLoading, deletePackage } = useSavedPackages();
  const assumptions = useSimulatorStore(s => s.assumptions);

  const [selectedPkg, setSelectedPkg] = useState<SavedPackage | null>(null);
  const [directions, setDirections] = useState<StrategicDirection[]>([]);
  const [chosenIndex, setChosenIndex] = useState<number | null>(null);
  const [openStep, setOpenStep] = useState(1);

  const {
    directionsData, materialsData,
    loadDirections, saveDirections, updateChosenDirection,
    loadMaterials, saveMaterials, updateMaterials,
  } = useMarketingPersistence(selectedPkg?.id ?? null);

  const config: PackageConfig | null = useMemo(() => {
    if (!selectedPkg) return null;
    return {
      name: selectedPkg.name,
      price: selectedPkg.price,
      paymentTerm: (selectedPkg.payment_term || 'monthlyZeroApr') as PackageConfig['paymentTerm'],
      enabledFeatures: selectedPkg.enabled_features || [],
    };
  }, [selectedPkg]);

  const pkgAssumptions = useMemo(() => selectedPkg?.assumptions ?? assumptions, [selectedPkg, assumptions]);
  const pnl = useMemo(() => config ? calculatePnL(config, pkgAssumptions) : null, [config, pkgAssumptions]);
  const buyerProxy = useMemo(() => config ? computeBuyerProxy(VERBATIM_DATA, config.price, config.enabledFeatures) : null, [config]);
  const segmentDemand = useMemo(() => config ? calculateSegmentPackageDemand(config.price, config.enabledFeatures, pkgAssumptions.featureScaling) : [], [config, pkgAssumptions]);

  // Saved content and ads for the current direction
  const savedContent = useMemo(() => {
    if (!materialsData?.materials?.content) return null;
    if (materialsData.direction_id !== directionsData?.id) return null;
    return materialsData.materials.content;
  }, [materialsData, directionsData]);

  const savedAds = useMemo(() => {
    if (!materialsData?.materials?.adImages) return undefined;
    if (materialsData.direction_id !== directionsData?.id) return undefined;
    return materialsData.materials.adImages as unknown as Record<string, AdResult> | undefined;
  }, [materialsData, directionsData]);

  // Load persisted data when package changes
  useEffect(() => {
    if (selectedPkg) {
      loadDirections();
      loadMaterials();
    } else {
      setDirections([]);
      setChosenIndex(null);
    }
  }, [selectedPkg?.id, loadDirections, loadMaterials]);

  useEffect(() => {
    if (directionsData) {
      setDirections(directionsData.directions ?? []);
      setChosenIndex(directionsData.chosen_index ?? null);
      if (directionsData.chosen_index !== null) setOpenStep(3);
    }
  }, [directionsData]);

  const handleSelectPkg = useCallback((pkg: SavedPackage) => {
    setSelectedPkg(pkg);
    setDirections([]);
    setChosenIndex(null);
    setOpenStep(2);
  }, []);

  const handleDirectionsGenerated = useCallback(async (dirs: StrategicDirection[]) => {
    setDirections(dirs);
    setChosenIndex(null);
    if (selectedPkg) await saveDirections(dirs);
  }, [selectedPkg, saveDirections]);

  const handleChooseDirection = useCallback(async (idx: number) => {
    setChosenIndex(idx);
    setOpenStep(3);
    if (directionsData?.id) await updateChosenDirection(directionsData.id, idx);
    else if (selectedPkg && directions.length > 0) await saveDirections(directions, idx);
  }, [directionsData, selectedPkg, directions, saveDirections, updateChosenDirection]);

  const handleContentGenerated = useCallback(async (content: string) => {
    if (selectedPkg && directionsData?.id) await saveMaterials(directionsData.id, content);
  }, [selectedPkg, directionsData, saveMaterials]);

  const handleAdsChanged = useCallback(async (ads: Record<string, AdResult>) => {
    if (materialsData?.id) {
      await updateMaterials(materialsData.id, ads as unknown as Record<string, unknown>);
    } else if (selectedPkg && directionsData?.id) {
      await saveMaterials(directionsData.id, savedContent || '', ads as unknown as Record<string, unknown>);
    }
  }, [materialsData, selectedPkg, directionsData, updateMaterials, saveMaterials, savedContent]);

  const step1Done = selectedPkg !== null;
  const step2Done = chosenIndex !== null && directions.length > 0;
  const chosenDirection = chosenIndex !== null ? directions[chosenIndex] : null;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone size={18} className="text-chart-teal" />
        <h2 className="text-lg font-bold text-primary">Marketing Strategy</h2>
        <InfoButton helpKey="marketingStrategy" size={14} />
      </div>
      <p className="text-sm text-muted mb-4">
        Build a complete marketing playbook for your saved packages. Select a package, choose a strategic direction, then generate data-driven materials.
      </p>

      <div className="card mb-3">
        <StepHeader step={1} title={step1Done ? `Package: ${selectedPkg!.name}` : 'Select a Saved Package'} done={step1Done} open={openStep === 1} onToggle={() => setOpenStep(openStep === 1 ? 0 : 1)} />
        {openStep === 1 && (
          <div className="pt-2">
            <SavedPackageSelector packages={savedPkgs} selectedId={selectedPkg?.id ?? null} onSelect={handleSelectPkg} onDelete={deletePackage} loading={pkgsLoading} />
          </div>
        )}
      </div>

      {step1Done && config && pnl && buyerProxy && openStep >= 1 && (
        <div className="space-y-3 mb-3">
          <MarketSizingPanel config={config} pnl={pnl} buyerProxy={buyerProxy} />
          <AudiencePersonaCard config={config} buyerProxy={buyerProxy} />
        </div>
      )}

      {step1Done && config && pnl && buyerProxy && (
        <div className="card mb-3">
          <StepHeader step={2} title={step2Done ? `Direction: ${chosenDirection!.name}` : 'Choose Strategic Direction'} done={step2Done} open={openStep === 2} onToggle={() => setOpenStep(openStep === 2 ? 0 : 2)} />
          {openStep === 2 && (
            <div className="pt-2">
              <StrategicDirections config={config} pnl={pnl} buyerProxy={buyerProxy} segmentDemand={segmentDemand} directions={directions} chosenIndex={chosenIndex} onDirectionsGenerated={handleDirectionsGenerated} onChoose={handleChooseDirection} />
            </div>
          )}
        </div>
      )}

      {step2Done && config && pnl && buyerProxy && chosenDirection && (
        <div className="card mb-3">
          <StepHeader step={3} title="Marketing Materials" done={!!savedContent} open={openStep === 3} onToggle={() => setOpenStep(openStep === 3 ? 0 : 3)} />
          {openStep === 3 && (
            <div className="pt-2">
              <MarketingWorkspace
                config={config} pnl={pnl} buyerProxy={buyerProxy} segmentDemand={segmentDemand}
                direction={chosenDirection}
                savedContent={savedContent}
                onContentGenerated={handleContentGenerated}
                savedAds={savedAds}
                onAdsChanged={handleAdsChanged}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
